export const PLACE = { latitude: -29.3364137, longitude: -53.6743829, timeZone: "America/Sao_Paulo" };
// The compass bearing the kiosk's front faces: square to BR-158, which OpenStreetMap runs at 176
// degrees past the shop. The model's front is +z and its right, up the road to Júlio de Castilhos, +x.
const FACING = 86;

const RAD = Math.PI / 180;

// Low-precision solar position (about 0.01 degrees): elevation above the horizon, the hour angle,
// negative before solar noon, and the azimuth, a compass bearing from -180 to 180, west negative.
// The azimuth wraps due south, where the sun never stands while it is up this far south, so the page
// sweeps it from one daytime bearing to another in a straight line.
export function sunAt(date, { latitude, longitude } = PLACE) {
  const days = date.getTime() / 86400000 - 10957.5;
  const anomaly = (357.529 + 0.98560028 * days) * RAD;
  const meanLongitude = 280.459 + 0.98564736 * days;
  const longitudeOnEcliptic = (meanLongitude + 1.915 * Math.sin(anomaly) + 0.02 * Math.sin(2 * anomaly)) * RAD;
  const obliquity = (23.439 - 0.00000036 * days) * RAD;
  const ascension = Math.atan2(Math.cos(obliquity) * Math.sin(longitudeOnEcliptic), Math.cos(longitudeOnEcliptic));
  const declination = Math.asin(Math.sin(obliquity) * Math.sin(longitudeOnEcliptic));
  const siderealHours = (18.697374558 + 24.06570982441908 * days) % 24;
  const hourAngle = Math.atan2(Math.sin(siderealHours * 15 * RAD + longitude * RAD - ascension),
    Math.cos(siderealHours * 15 * RAD + longitude * RAD - ascension));
  const elevation = Math.asin(Math.sin(latitude * RAD) * Math.sin(declination)
    + Math.cos(latitude * RAD) * Math.cos(declination) * Math.cos(hourAngle));
  const azimuth = Math.atan2(-Math.sin(hourAngle),
    Math.tan(declination) * Math.cos(latitude * RAD) - Math.cos(hourAngle) * Math.sin(latitude * RAD));
  return { elevation: elevation / RAD, hourAngle: hourAngle / RAD, azimuth: azimuth / RAD };
}

// Keyed by the sun's real elevation in degrees. Day is the Blender renders' world; the rest were
// matched by eye, ending in the brand navy at night.
const SKIES = [
  { at: -12, zenith: "#10153F", horizon: "#27306E", ground: "#1A1D34", light: "#9DB0FF", sun: 1.5, ambient: 0.5, exposure: 1.3 },
  { at: -5, zenith: "#28306C", horizon: "#7A6594", ground: "#35304A", light: "#B7A8E8", sun: 1.1, ambient: 0.48, exposure: 1.15 },
  { at: 0, zenith: "#66729F", horizon: "#EE9F7D", ground: "#655C68", light: "#FF8E52", sun: 1.8, ambient: 0.5, exposure: 1 },
  { at: 7, zenith: "#98A9D0", horizon: "#F3C996", ground: "#857B71", light: "#FFB46E", sun: 3.2, ambient: 0.7, exposure: 1 },
  { at: 20, zenith: "#9FB6D2", horizon: "#E6D8C2", ground: "#8E8A80", light: "#FFD9AE", sun: 4, ambient: 0.85, exposure: 1 },
];

// Below the horizon the light is the moon's, fixed high on the front right, so the kiosk still
// throws a shadow and its white front still reads.
const MOON = { elevation: 38, azimuth: -30 };
const TWILIGHT = { day: -2, night: -8 };
// The sun's elevation, in degrees, below which the knob reads dusk, and then night. At dusk the lamps come on
// together, as on a photocell, and once the shop has shut too the tractor calls the driver aboard.
const DUSK = { from: 6, night: -6 };

// The moments the page's knob stages instead of the clock: the sun at the shop on the September
// equinox, when it rises in front of the kiosk and sets behind it.
export const MOMENTS = {
  dia: sunAt(new Date("2026-09-23T10:00:00-03:00")),
  "por-do-sol": sunAt(new Date("2026-09-23T18:25:00-03:00")),
  noite: sunAt(new Date("2026-09-23T20:30:00-03:00")),
};

// The moment the knob shows while it follows the clock.
export function momentAt({ elevation }) {
  if (elevation > DUSK.from) return "dia";
  return elevation > DUSK.night ? "por-do-sol" : "noite";
}

// `sun` is sunAt()'s result or a moment; `rain`, `frost` and `cloud` run from 0 to 1.
export function daylight({ elevation, azimuth }, { rain = 0, frost = 0, cloud = 0 } = {}) {
  const overcast = Math.max(rain, CLOUDY * cloud);
  const sky = weathered(blend(elevation), overcast, frost);
  const night = clamp((TWILIGHT.day - elevation) / (TWILIGHT.day - TWILIGHT.night), 0, 1);
  // The light's azimuth is clockwise from the kiosk's front, as the viewer takes it. Through dusk it
  // stays 4 degrees up rather than shine through the floor.
  const sun = { elevation: Math.max(elevation, 4), azimuth: azimuth - FACING };
  return {
    elevation,
    night,
    lamps: momentAt({ elevation }) === "dia" ? 0 : 1,
    sky,
    weather: { rain, frost, overcast },
    light: {
      elevation: lerp(sun.elevation, MOON.elevation, night),
      azimuth: lerp(sun.azimuth, MOON.azimuth, night),
    },
  };
}

function blend(elevation) {
  const upper = SKIES.findIndex((sky) => sky.at >= elevation);
  if (upper === 0) return { ...SKIES[0] };
  if (upper === -1) return { ...SKIES.at(-1) };
  const a = SKIES[upper - 1], b = SKIES[upper];
  const t = (elevation - a.at) / (b.at - a.at);
  const mixed = {};
  for (const key of Object.keys(a)) {
    mixed[key] = typeof a[key] === "string" ? mixColour(a[key], b[key], t) : lerp(a[key], b[key], t);
  }
  return mixed;
}

// Cloud greys the sky toward its own brightness and dims the sun, and a rain cloud a little more
// than a dry one. A frost comes on a clear, still night after a cold front, so frost cools the
// colours and whitens the light.
const CLOUDY = 0.8;
const OVERCAST_BLUE = [0.94, 0.98, 1.08];
const FROSTED = [0.88, 0.97, 1.12];

function weathered(sky, overcast, frost) {
  const out = { ...sky };
  for (const key of ["zenith", "horizon", "ground"]) {
    out[key] = mixColour(out[key], tint(out[key], FROSTED), frost * 0.5);
    out[key] = mixColour(out[key], tint(grey(out[key], key === "zenith" ? 0.82 : 0.92), OVERCAST_BLUE), overcast * 0.85);
  }
  out.light = mixColour(mixColour(out.light, "#EEF3FF", frost * 0.45), grey(out.light, 1), overcast * 0.8);
  out.sun *= 1 - 0.72 * overcast - 0.1 * frost;
  out.ambient *= 1 + 0.3 * overcast;
  return out;
}

function channels(hex) {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex(rgb) {
  return `#${rgb.map((c) => Math.round(clamp(c, 0, 255)).toString(16).padStart(2, "0")).join("")}`;
}

// Halfway between luma and the brightest channel: luma alone turns the navy night sky black.
function grey(hex, brightness) {
  const [r, g, b] = channels(hex);
  const level = ((0.2126 * r + 0.7152 * g + 0.0722 * b + Math.max(r, g, b)) / 2) * brightness;
  return toHex([level, level, level]);
}

function tint(hex, factors) {
  return toHex(channels(hex).map((c, i) => c * factors[i]));
}

function mixColour(from, to, t) {
  const a = parseInt(from.slice(1), 16), b = parseInt(to.slice(1), 16);
  const channel = (shift) => Math.round(lerp((a >> shift) & 255, (b >> shift) & 255, t));
  return `#${((channel(16) << 16) | (channel(8) << 8) | channel(0)).toString(16).padStart(6, "0")}`;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clamp(value, low, high) {
  return Math.min(high, Math.max(low, value));
}
